/**
 * Height parsing helpers.
 *
 * `parseHeightString` accepts the casual ways practitioners type height
 * and normalizes to inches: `5'10"`, `5'10`, `5 10`, `5ft 10in`, `70`,
 * `70.5`. Returns null when the input doesn't parse.
 *
 * `inchesToFeetInches` is the inverse for the dual ft+in input mode.
 */

export const parseHeightString = (raw: string): number | null => {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  if (!s) return null;

  // Pattern 1: 5'10", 5'10, 5' 10", 5'10.5"
  const fi1 = s.match(/^(\d+)\s*['′ft]\s*(\d+(?:\.\d+)?)?\s*["″i]*n*$/);
  if (fi1) {
    const ft = parseInt(fi1[1], 10);
    const inch = fi1[2] ? parseFloat(fi1[2]) : 0;
    if (Number.isFinite(ft) && Number.isFinite(inch)) {
      return ft * 12 + inch;
    }
  }

  // Pattern 2: 5 10  (space-separated feet+inches)
  const fi2 = s.match(/^(\d+)\s+(\d+(?:\.\d+)?)$/);
  if (fi2) {
    const ft = parseInt(fi2[1], 10);
    const inch = parseFloat(fi2[2]);
    // Heuristic: treat as feet+inches only when the second number is
    // a plausible inches-within-a-foot value (0–11.99).
    if (Number.isFinite(ft) && Number.isFinite(inch) && inch < 12 && ft <= 8) {
      return ft * 12 + inch;
    }
  }

  // Pattern 3: plain inches (70, 70.5)
  const plain = s.match(/^(\d+(?:\.\d+)?)\s*(?:in|inch|inches)?$/);
  if (plain) {
    const n = parseFloat(plain[1]);
    if (Number.isFinite(n)) return n;
  }

  return null;
};

export interface FeetInches {
  ft: number;
  in: number;
}

export const inchesToFeetInches = (inches: number | null | undefined): FeetInches | null => {
  if (inches == null || !Number.isFinite(inches)) return null;
  const ft = Math.floor(inches / 12);
  const inch = Math.round((inches - ft * 12) * 10) / 10;
  return { ft, in: inch };
};

export const feetInchesToInches = (ft: number | null, inch: number | null): number | null => {
  const f = ft ?? 0;
  const i = inch ?? 0;
  if (!Number.isFinite(f) || !Number.isFinite(i)) return null;
  if (f === 0 && i === 0) return null;
  return f * 12 + i;
};
